// src/components/tiptap/verticalNav.ts
// Deterministic arrow-key caret navigation for the reporter editor.
//
// Why this exists: every finding renders as a FieldNode chip whose ChipView
// mounts an inner contentEditable span (so a word can be edited in place).
// Each of those spans is a SEPARATE browser editing host that ProseMirror does
// not own, so native vertical caret movement (Up/Down) across them is
// unreliable — the caret hops between chips on the same visual line or freezes
// instead of moving line-by-line, and the report is made almost entirely of
// chips. That is issue #48 (Rad04: "the arrow keys seem to randomly jump
// through the report section of the drafter instead of just move up and down").
//
// The fix takes over Up/Down and computes the target caret position from
// screen coordinates (posAtCoords / coordsAtPos), always landing a normal PM
// text selection on the adjacent visual line and returning focus to the editor
// root. posAtCoords resolves the position ADJACENT to a chip atom, never inside
// its editing host, which is exactly what keeps the caret under PM control.
//
// Two entry points call in here:
//   - VerticalArrowNav (below) handles the caret-in-prose case (focus on the
//     PM root), where editor-level key props fire normally.
//   - ChipView.handleKeyDown handles the caret-in-chip case, because Tiptap's
//     ReactNodeView.stopEvent suppresses editor-level key props for events
//     that originate inside a chip.

import { Extension, type Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { CHIP_INNER_CLASS } from "./marks";

// Goal column (preferred X) for line-by-line movement, matching a native text
// editor: the horizontal position is remembered across consecutive Up/Down
// presses so descending from a long line to a short one and back preserves the
// column. Validated by a continuity check against the last landing position —
// any other caret move (typing, click, Tab, a horizontal arrow via
// moveCaretToPos) leaves selection.from != lastLandingPos, so the next vertical
// press recomputes the column from the live caret instead of a stale value.
//
// Held per-editor in `editor.storage.verticalArrowNav` (not module scope):
// PrototypeReport and TemplateEditor both register VerticalArrowNav, and
// module-global state would let one editor's goal column leak into the other
// when their selection.from values coincidentally align.
export interface VerticalArrowNavStorage {
  goalColumnX: number | null;
  lastLandingPos: number | null;
}

// Tiptap 3 tightened `editor.storage` typing — declare this extension's
// storage shape so consumers read it without casts (merges with the `field`
// storage declared in ./marks.ts and `regenerateImpression` in ./extensions.ts).
declare module "@tiptap/core" {
  interface Storage {
    verticalArrowNav: VerticalArrowNavStorage;
  }
}

function navStorage(editor: Editor): VerticalArrowNavStorage {
  return editor.storage.verticalArrowNav;
}

// Current caret's viewport rect. When DOM focus is inside a chip's inner
// editing host, PM's selection is stale (it never saw the click into the chip),
// so read the live DOM caret; otherwise PM owns the selection and coordsAtPos
// is authoritative — a DOM-selection readback is degenerate at atom-adjacent
// caret positions.
function caretRect(view: EditorView): { top: number; bottom: number; left: number } | null {
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (active instanceof HTMLElement && active.classList.contains(CHIP_INNER_CLASS)) {
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
      // Reject only a fully-degenerate all-zero rect (an unrendered/detached
      // range), falling through to PM's coordsAtPos. A legitimate caret at the
      // top-left of the viewport has top/left 0 but a non-zero bottom (its line
      // height), so it is NOT treated as degenerate.
      const degenerate =
        !rect || (rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.height === 0);
      if (!degenerate) {
        return { top: rect.top, bottom: rect.bottom, left: rect.left };
      }
    }
  }
  const c = view.coordsAtPos(view.state.selection.from);
  return { top: c.top, bottom: c.bottom, left: c.left };
}

function applySelection(view: EditorView, pos: number, bias: 1 | -1): number {
  const clamped = Math.max(0, Math.min(pos, view.state.doc.content.size));
  const selection = TextSelection.near(view.state.doc.resolve(clamped), bias);
  view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
  view.focus();
  return view.state.selection.from;
}

// Move the PM selection to `pos` (clamped to the nearest selectable text
// position with the given search bias) and return focus to the editor root,
// blurring any chip inner span the caret was in. Used for horizontal chip-exit
// (Left at a chip's start / Right at its end); resets the vertical goal column
// because this is not a line move.
export function moveCaretToPos(editor: Editor, pos: number, bias: 1 | -1): void {
  applySelection(editor.view, pos, bias);
  const store = navStorage(editor);
  store.goalColumnX = null;
  store.lastLandingPos = null;
}

// Probe outward from just past the current line at column `x` until
// posAtCoords resolves a caret on a different visual line, and return that PM
// position. Small steps cross wraps, section labels, and chip atoms without
// entering their editing hosts.
//
// The probe is bounded by the editor's visible content rect, NOT a fixed
// multiple of lineHeight: report section gaps (FINDINGS↔IMPRESSION, organ
// headings with top margins) can span several line-heights, and a fixed cap
// would miss the next line across a large gap and return null — which makes
// navigateByLine defer to the exact "random jump" native behavior this fix
// exists to replace (issue #48). Probing to the content edge means null is
// returned only at the true top/bottom of the rendered report, where native
// Up/Down is a harmless no-op rather than a wrong-line jump.
function probeLinePos(
  view: EditorView,
  x: number,
  origin: { top: number; bottom: number },
  lineHeight: number,
  dir: "up" | "down",
): number | null {
  const down = dir === "down";
  const sign = down ? 1 : -1;
  const startTop = (down ? origin.bottom : origin.top) + sign * 2;
  const stepPx = 3;
  const bounds = view.dom.getBoundingClientRect();
  const limit = down ? bounds.bottom : bounds.top;
  for (let top = startTop; down ? top <= limit : top >= limit; top += sign * stepPx) {
    const found = view.posAtCoords({ left: x, top });
    if (!found) continue;
    const c = view.coordsAtPos(found.pos);
    const onNewLine = down
      ? c.top > origin.top + lineHeight * 0.5
      : c.bottom < origin.bottom - lineHeight * 0.5;
    if (onNewLine) return found.pos;
  }
  return null;
}

// Move the caret one visual line up/down at the current goal column. Returns
// true when it moved (caller should preventDefault); false at the top/bottom
// edge (caller lets the default happen).
export function navigateByLine(editor: Editor, dir: "up" | "down"): boolean {
  const view = editor.view;
  const store = navStorage(editor);
  const origin = caretRect(view);
  if (!origin) return false;
  const lineHeight = Math.max(origin.bottom - origin.top, 12);
  const continuation =
    store.lastLandingPos !== null && view.state.selection.from === store.lastLandingPos;
  const x = continuation && store.goalColumnX !== null ? store.goalColumnX : origin.left;
  const targetPos = probeLinePos(view, x, origin, lineHeight, dir);
  if (targetPos === null) return false;

  const landed = applySelection(view, targetPos, dir === "down" ? 1 : -1);
  store.goalColumnX = x;
  store.lastLandingPos = landed;
  return true;
}

// Editor-level binding for the caret-in-prose case (focus on the PM root). The
// caret-in-chip case is handled in ChipView.handleKeyDown — Tiptap's
// ReactNodeView.stopEvent suppresses editor-level key props for events that
// originate inside a chip's inner editing host.
export const VerticalArrowNav = Extension.create({
  name: "verticalArrowNav",

  addStorage(): VerticalArrowNavStorage {
    return { goalColumnX: null, lastLandingPos: null };
  },

  addKeyboardShortcuts() {
    return {
      ArrowUp: () => navigateByLine(this.editor, "up"),
      ArrowDown: () => navigateByLine(this.editor, "down"),
    };
  },
});
